import { formatCurrency } from "../../helpers/currency-formatting-util.js";
import { isNullOrEmpty } from "../../helpers/object-util.js";
import { widget } from "@foretagsplatsen/widgetjs";

/**
 * Compares a key figure for the current period against the same
 * keyfigure for the best periods. The total number of displayed
 * periods can be configured with the `numberOfItemsToShow` variable.
 * For example, "top 5 profit/loss months" would display four months
 * with the highest profit, and the current month, in this order.
 * If the current month is in the top four, then the five months
 * displayed are the best five months.
 *
 * @param {integer} [spec.numberOfItemsToShow] - Number of periods to
 * show. Must be greater than or equal to 2.
 */
export default widget.subclass((that, my) => {
	my.initialize = function ({ numberOfItemsToShow = 6 }) {
		my.super(...arguments);

		my.numberOfItemsToShow = numberOfItemsToShow;
	};

	/**
	 * @param {KeyFigure} keyFigure - KeyFigure to show
	 * @param {keyFigureValue[]} keyFigureValues - Values of this key figure
	 * @param {Period} period - Period corresponding to the values
	 */
	that.show = function (keyFigure, keyFigureValues, period) {
		my.period = period;
		my.keyFigure = keyFigure;

		my.keyFigureValues = keyFigureValues
			.filter(
				(keyFigureValue) =>
					keyFigureValue && keyFigureValue.isForActivePeriod(),
			)
			.sort((a, b) => my.getValueOrDefault(b) - my.getValueOrDefault(a));

		that.update();
	};

	that.isEmpty = function () {
		return isNullOrEmpty(my.keyFigureValues);
	};

	my.itemsToShow = function () {
		if (my.keyFigureValues.length <= my.numberOfItemsToShow) {
			return my.keyFigureValues;
		}

		return [...my.topItems(), my.currentItemOrNextTopOne()];
	};

	my.topItems = function () {
		return my.keyFigureValues.slice(0, my.numberOfItemsToShow - 1);
	};

	my.currentItemOrNextTopOne = function () {
		const currentItem = my.getCurrentItem();

		if (my.topItems().includes(currentItem)) {
			return my.keyFigureValues[my.numberOfItemsToShow - 1];
		}
		return currentItem;
	};

	my.getCurrentItem = function () {
		return my.keyFigureValues.find(
			(value) => value.getPeriod() === my.period,
		);
	};

	my.isCurrent = function (item) {
		return my.getCurrentItem() === item;
	};

	my.getPosition = function (item) {
		return my.keyFigureValues.indexOf(item) + 1;
	};

	/**
	 * We treat null values as zero during display and sorting
	 *
	 * This is a special case for topcharts: In general case,
	 * sorting order of keyfigure values is context dependent
	 * because of null values. Treating them as zero might depict
	 * a misleading order for some cases: keyfigure values for inactive periods,
	 * keyfigure values that only yield negative values, etc.
	 *
	 * This solution is based on Anders' feedback. We might have to revisit it
	 * when we enable users to freely choose topchart keyfigures.
	 */
	my.getValueOrDefault = function (keyFigureValue) {
		return keyFigureValue.getValue() || 0;
	};

	that.renderContentOn = function (html) {
		html.div().render((html) => {
			if (that.isEmpty()) {
				return;
			}
			my.renderTopChartBody(html);
		});
	};

	my.renderTopChartBody = function (html) {
		html.table(
			{ class: "table table-striped" },
			html.tbody(my.itemsToShow().map(my.renderTopChartItem)),
		);
	};

	my.renderTopChartItem = function (item) {
		return function (html) {
			if (my.isCurrent(item)) {
				html.tr(
					{ klass: "current-period" },
					html.td(
						html.strong(
							`${my.getPosition(item)}: `,
							my.titleRenderer(item),
						),
					),
					html.td(
						{ class: "text-right" },
						html.strong(formatCurrency(my.getValueOrDefault(item))),
					),
				);
			} else {
				html.tr(
					html.td(
						`${my.getPosition(item)}: `,
						my.titleRenderer(item),
					),
					html.td(
						{ class: "text-right" },
						formatCurrency(my.getValueOrDefault(item)),
					),
				);
			}
		};
	};

	my.titleRenderer = function (item) {
		return function (html) {
			if (my.keyFigure.isVisible()) {
				html.a(
					{
						href: my.linkTo("single-kpi", {
							period: item.getPeriod().toString(),
							keyFigure: my.keyFigure.getId(),
						}),
					},
					item.getPeriod().toSmartString(),
				);
			} else {
				html.render(item.getPeriod().toSmartString());
			}
		};
	};
});
